/**
 * graphicsManager.js - Graphics Layer Management System
 *
 * Handles the 8-layer graphics system for user-uploaded graphics on the model.
 * This is separate from decals and uses UV-based projection.
 */

import * as THREE from 'three';
import {
    uploadedGraphics,
    graphicLayers,
    graphicLayerMap,
    groupDecalSettings,
    lockedGroupDecals,
    pendingGroupTexture,
    setPendingGroupTexture,
    setActiveGroup,
    setActiveSide,
    setActiveSideKey,
    currentEditingGraphic,
    setCurrentEditingGraphic
} from './state.js';
import { updateAllMaterialsWithGraphicLayers } from './decalManager.js';
import { requestRender } from './sceneManager.js';
import { openCfgSlideItem, closeCfgSlideItem } from './utils.js';

// Re-export for consistency
export { updateAllMaterialsWithGraphicLayers };

/**
 * Open the graphics slide and make a specific graphic editable
 * @param {string} graphicId - The ID of the graphic to edit
 */
export function openGraphicForEditing(graphicId) {
    // Find the graphic object
    const graphic = uploadedGraphics.find(g => g.id === graphicId);
    if (!graphic) {
        return false;
    }

    // Find and open the graphics slide
    const graphicsSlide = document.querySelector('.cfgSlideItem[data-type="graphics-upload"]');
    if (!graphicsSlide) {
        return false;
    }

    // Close all other open slides first - exactly like the normal click handler does
    const panelContainer = document.querySelector('.configuratorPanel');

    if (panelContainer) {
        const allSlides = panelContainer.querySelectorAll('.cfgSlideItem');

        allSlides.forEach((otherSlide, index) => {
            if (otherSlide !== graphicsSlide) {
                const otherContent = otherSlide.querySelector('.cfgSlideItem__content');

                if (otherContent && otherContent.offsetHeight > 0) {
                    closeCfgSlideItem(otherSlide);

                    // Sofort display none setzen
                    otherContent.style.display = 'none';
                }
            }
        });
    }

    // Open the slide if not already open
    const content = graphicsSlide.querySelector('.cfgSlideItem__content');
    const isAlreadyOpen = graphicsSlide.classList.contains('is-active') ||
                         (content && content.offsetHeight > 0);
    if (!isAlreadyOpen) {
        openCfgSlideItem(graphicsSlide);
    }

    // Find the graphic row in the UI
    const graphicRow = document.querySelector(`.graphic-row[data-graphic-id="${graphicId}"]`);
    if (!graphicRow) {
        return false;
    }

    // Deactivate all other graphics first
    // panelContainer already declared above
    panelContainer?.querySelectorAll('.graphic-row').forEach(row => {
        const rowGraphicId = row.dataset.graphicId;
        const rowGraphic = uploadedGraphics.find(g => g.id === rowGraphicId);
        if (rowGraphic && rowGraphic.isEditing) {
            rowGraphic.isEditing = false;
            const rowControls = row.querySelector('.graphic-controls');
            const rowEditBtn = row.querySelector('.edit-graphic-btn');
            if (rowControls) rowControls.style.display = 'none';
            if (rowEditBtn) rowEditBtn.innerHTML = '<i class="icon icon-edit"></i>';
            row.style.flexWrap = '';

            // Lock the graphic
            lockedGroupDecals.set(rowGraphic.id, true);
        }
    });

    // Activate editing mode for the clicked graphic
    graphic.isEditing = true;
    setCurrentEditingGraphic(graphic);

    // Update UI
    const graphicControls = graphicRow.querySelector('.graphic-controls');
    const editBtn = graphicRow.querySelector('.edit-graphic-btn');
    if (graphicControls) graphicControls.style.display = 'block';
    if (editBtn) editBtn.innerHTML = '<i class="icon icon-check"></i>';
    graphicRow.style.flexWrap = 'wrap';

    // Unlock the graphic for editing
    lockedGroupDecals.set(graphic.id, false);
    setActiveGroup(graphic.id);
    setActiveSide(null);
    setActiveSideKey(null);

    // Update shader uniforms for editing outline
    updateAllMaterialsWithGraphicLayers();
    requestRender();

    return true;
}
